import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Sparkles, Search } from "lucide-react";
import SerpPreview from "./SerpPreview";

interface Props {
  title: string; slug: string; metaDescription: string; keyword: string; secondaryKeyword: string;
  onTitle: (v: string) => void;
  onSlug: (v: string) => void;
  onMeta: (v: string) => void;
  onKeyword: (v: string) => void;
  onSecondaryKeyword: (v: string) => void;
  onAiFix: (field: "title" | "meta" | "slug") => void;
  aiBusy?: string | null;
  slugLocked?: boolean;
}

const counterColor = (len: number, min: number, max: number) =>
  len === 0 ? "bg-muted" : len < min ? "bg-amber-500" : len > max ? "bg-destructive" : "bg-emerald-500";

const MetaPanel = ({ title, slug, metaDescription, keyword, secondaryKeyword, onTitle, onSlug, onMeta, onKeyword, onSecondaryKeyword, onAiFix, aiBusy, slugLocked }: Props) => {
  const tLen = title.length;
  const mLen = metaDescription.length;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Search className="h-4 w-4 text-primary" /> Aperçu Google & Meta SEO
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <SerpPreview title={title} slug={slug} metaDescription={metaDescription} />

        <div>
          <label className="text-xs font-medium">Mot-clé principal (focus)</label>
          <Input value={keyword} onChange={(e) => onKeyword(e.target.value)} placeholder="ex : transfert privé Marrakech" />
        </div>

        <div>
          <label className="text-xs font-medium">Mot-clé secondaire</label>
          <Input value={secondaryKeyword} onChange={(e) => onSecondaryKeyword(e.target.value)} placeholder="ex : navette aéroport Marrakech" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium">Titre SEO</label>
            <span className={`text-xs ${tLen < 50 ? "text-amber-600" : tLen > 60 ? "text-destructive" : "text-emerald-600"}`}>{tLen}/60</span>
          </div>
          <div className="flex gap-2">
            <Input value={title} onChange={(e) => onTitle(e.target.value)} />
            <Button size="sm" variant="outline" onClick={() => onAiFix("title")} disabled={aiBusy === "title"}>
              <Sparkles className={`h-3.5 w-3.5 ${aiBusy === "title" ? "animate-pulse" : ""}`} />
            </Button>
          </div>
          <div className="h-1 rounded-full bg-muted mt-1 overflow-hidden">
            <div className={`h-full ${counterColor(tLen, 50, 60)}`} style={{ width: `${Math.min((tLen / 60) * 100, 100)}%` }} />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium">Meta description</label>
            <span className={`text-xs ${mLen < 120 ? "text-amber-600" : mLen > 158 ? "text-destructive" : "text-emerald-600"}`}>{mLen}/158</span>
          </div>
          <div className="flex gap-2">
            <Textarea rows={2} value={metaDescription} onChange={(e) => onMeta(e.target.value)} />
            <Button size="sm" variant="outline" onClick={() => onAiFix("meta")} disabled={aiBusy === "meta"}>
              <Sparkles className={`h-3.5 w-3.5 ${aiBusy === "meta" ? "animate-pulse" : ""}`} />
            </Button>
          </div>
          <div className="h-1 rounded-full bg-muted mt-1 overflow-hidden">
            <div className={`h-full ${counterColor(mLen, 120, 158)}`} style={{ width: `${Math.min((mLen / 158) * 100, 100)}%` }} />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium">Slug (URL)</label>
            <span className="text-xs text-muted-foreground">{slug.length}/75</span>
          </div>
          <div className="flex gap-2">
            <Input
              value={slug}
              readOnly={slugLocked}
              disabled={slugLocked}
              onChange={(e) => onSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-"))}
            />
            <Button size="sm" variant="outline" onClick={() => onAiFix("slug")} disabled={aiBusy === "slug" || slugLocked}>
              <Sparkles className={`h-3.5 w-3.5 ${aiBusy === "slug" ? "animate-pulse" : ""}`} />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">/guide/blog/{slug}</p>
          {slugLocked && (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              🔒 Permalink verrouillé — protégé pour préserver le SEO de l'article publié.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MetaPanel;